export interface FocusGroupParams {
    onFocus? : () => void;
    onBlur?  : () => void;
}

export interface FocusEventHandlers {
    onFocus() : void;
    onBlur () : void;
}

// Symbols for use w/in this module only
const
    ON_PARTICIPANT_FOCUS = Symbol('onParticipantFocus'),
    ON_PARTICIPANT_BLUR  = Symbol('onParticipantBlur' );

/**
 * A utility to treat a set of DOM elements as a group for focus and blur purposes.
 * Lets you give the group a blur and/or focus handler such that blurring or focusing the entire group will
 * fire the handler but tabbing between elements in the group won't. Note that this is different from the
 * behavior if you simply add an onBlur or onFocus handler to some parent element of the entire group; in that
 * case all blurs or focuses from inside the group will bubble up to the parent, just like a normal event
 * propagation chain.
 */
export class FocusGroup {
    private readonly onFocus? : () => void;
    private readonly onBlur? : () => void;

    private focusedParticipant : FocusGroupParticipant | undefined;
    private blurTimeout : number | undefined;

    constructor({ onFocus, onBlur } : FocusGroupParams) {
        this.onFocus = onFocus;
        this.onBlur = onBlur;
    }

    /**
     * Enroll one or more new participants in this @see FocusGroup.
     */
    enroll() {
        return new FocusGroupParticipant(this);
    }

    // Internal methods only callable from w/in this module (hence the computed symbol-valued method names).
    [ON_PARTICIPANT_FOCUS](participant : FocusGroupParticipant) {
        window.clearTimeout(this.blurTimeout);
        const wasUnfocused = this.focusedParticipant === undefined;
        this.focusedParticipant = participant;
        if (wasUnfocused) this.onFocus?.();
    }

    [ON_PARTICIPANT_BLUR](participant : FocusGroupParticipant) {
        // Tolerate onParticipantFocus and onParticipantBlur firing in either order (new focus before old blur
        // or old blur before new focus). Native DOM events fire in the order blur on old focused element then
        // focus on new focused element, but if we chain focus groups together, then onParticipantBlur on the
        // downstream FocusGroup can fire after a timeout and thus AFTER onParticipantFocus on the new focused
        // element. So... if a new participant has already gotten focus, then no need for any further action.
        // Otherwise, handle blur in a timeout to see if onParticipantFocus is about to fire.
        if (this.focusedParticipant === participant) {
            window.clearTimeout(this.blurTimeout);
            this.blurTimeout = window.setTimeout(() => {
                this.focusedParticipant = undefined;
                this.onBlur?.();
            });
        }
    }
}

export class FocusGroupParticipant {
    /**
     * Use these event handlers to bind this @see FocusGroupParticipant to a DOM element. Intended to be
     * conveniently consumed via destructuring, e.g. `<input {...someFocusGroupParticipant.events} />`.
     */
    readonly events : FocusEventHandlers;

    constructor(focusGroup : FocusGroup) {
        this.events = {
            onFocus: () => focusGroup[ON_PARTICIPANT_FOCUS](this),
            onBlur:  () => focusGroup[ON_PARTICIPANT_BLUR ](this)
        };
    }
}



// WEBPACK FOOTER //
// ./lib/focusGroup.ts